// EP-135 Tier 1 — Governance i18n (meetings, resolutions, committee).
// Used by MeetingListPage, MeetingDetailPage, CommitteePage.
export default {
  nav: {
    meetings: 'Meetings',
    committee: 'Committee',
  },

  meetings: {
    title: 'Meetings',
    description: 'AGMs, EGMs and committee meetings — records of the juristic-person governance.',

    labels: {
      title: 'Title',
      meeting_type: 'Type',
      status: 'Status',
      meeting_at: 'Date',
      venue: 'Venue',
      agenda: 'Agenda',
      call_number: 'Call #',
      notice_lead_days: 'Notice lead (days)',
      meeting_url: 'Online meeting URL',
      meeting_access_note: 'Access note',
      minutes_body: 'Minutes body',
      quorum_basis: 'Quorum basis',
      property: 'Property',
      certified_at: 'Certified at',
    },

    types: {
      annual_general_meeting: 'Annual General Meeting',
      extraordinary: 'Extraordinary General Meeting',
      committee: 'Committee meeting',
    },

    statuses: {
      draft: 'Draft',
      noticed: 'Notice sent',
      in_progress: 'In progress',
      voting_closed: 'Voting closed',
      closed: 'Closed',
      certified: 'Certified',
    },

    actions: {
      new_meeting: 'New meeting',
      add_resolution: 'Add resolution',
      new_notice: 'New notice',
      send_for_certification: 'Send for certification',
      close_voting: 'Close voting',
      carry_over: 'Carry over to next call',
      view_audit_log: 'View audit log',
      snapshot_attendees: 'Snapshot attendees',
      add_attendee: 'Add attendee',
      record_vote: 'Record vote',
      back_to_meetings: 'Back to meetings',
    },

    empty: {
      loading: 'Loading minutes…',
      no_meetings: 'No meetings yet',
      no_meetings_description:
        'Create your first AGM, EGM or committee meeting to start tracking governance actions.',
      no_resolutions: 'No resolutions yet',
      no_resolutions_description:
        'Add a resolution when the agenda item needs a recorded decision.',
      no_notices: 'No notices served yet',
      no_notices_description:
        'Send a notice to record proof of service on every shared owner or committee member.',
      no_deliveries: 'No delivery rows for this notice.',
      could_not_load_notice: 'Could not load notice details.',
    },

    messages: {
      created: 'Meeting created',
      updated: 'Meeting updated',
      sent_for_certification: 'Meeting sent for certification',
      voting_closed: 'Voting closed — tally frozen on every resolution',
      carried_over: 'New call-2 meeting created',
      attendees_snapshot: 'Attendees snapshotted from the active OwnerContract roll',
      attendee_added: 'Attendee added',
      vote_recorded: 'Vote recorded',
      resolution_added: 'Resolution added',
      resolution_updated: 'Resolution updated',
      notice_created: 'Notice served',
      not_ready_for_certification: 'Only a closed or in-progress meeting can be sent for certification.',
      not_in_progress_voting: 'Voting can only be closed while the meeting is in progress.',
      not_call_one_carry_over: 'Only a call-1 meeting can carry over to a call-2 meeting.',
      carry_over_date_required: 'A meeting_at for the call-2 meeting is required.',
    },

    filters: {
      all_types: 'All types',
      all_statuses: 'All statuses',
      by_date: 'Newest first',
    },
  },

  resolutions: {
    title: 'Resolutions',
    labels: {
      seq: '#',
      title: 'Title',
      body: 'Body',
      note: 'Note',
      decided_by: 'Decided by',
      status: 'Outcome',
      threshold_rule: 'Threshold',
      threshold_basis: 'Basis',
      secret: 'Secret ballot',
      from_agenda: 'From agenda',
    },

    decided_by: {
      vote: 'Vote',
      consensus: 'Consensus',
      chair_ruling: 'Chair ruling',
      noted: 'Noted',
    },

    statuses: {
      proposed: 'Proposed',
      carried: 'Carried',
      defeated: 'Defeated',
      withdrawn: 'Withdrawn',
      deferred: 'Deferred',
    },

    threshold_basis: {
      of_attendees: 'Of attendees',
      of_all_owners: 'Of all owners',
    },

    messages: {
      certified_immutable: 'Certified resolutions are immutable; amend via a follow-up resolution.',
      not_deletable: 'Resolutions are records of a legal proceeding; they amend, they do not disappear.',
    },
  },

  committee: {
    title: 'Committee',
    description: 'Active and past committee members elected by the AGM.',

    labels: {
      customer: 'Member',
      role: 'Role',
      elected_on: 'Elected on',
      term_starts_on: 'Term starts',
      term_ends_on: 'Term ends',
      ended_on: 'Term ended',
      ended_reason: 'End reason',
      not_qualifying: 'No longer qualifying',
      qualifies: 'Qualifying',
    },

    roles: {
      chair: 'Chair',
      vice: 'Vice-chair',
      secretary: 'Secretary',
      treasurer: 'Treasurer',
      member: 'Member',
    },

    actions: {
      new_member: 'New committee member',
      end_term: 'End term',
    },

    empty: {
      no_members: 'No committee members yet',
      no_members_description:
        'Add a committee member elected by the most recent AGM.',
    },

    messages: {
      created: 'Committee member added',
      updated: 'Committee member updated',
      term_ended: 'Term ended',
      not_a_shared_owner: 'A committee member must hold an active OwnerContract on this account.',
      end_term_title: 'End term',
      end_term_confirm: 'End {{name}}’s committee term? This action cannot be undone.',
      reason_required: 'An end-of-term reason is required.',
      end_reason_label: 'Reason (e.g., "AGM 2571 resolution #3")',
      end_date_label: 'End date',
    },

    filters: {
      all_roles: 'All roles',
      active: 'Active',
      ended: 'Ended',
      show_active: 'Active only',
    },
  },

  // EP-135 Tier 2 — Notices (proof of service). One notice creates
  // one delivery row per audience member; the row's existence is
  // the proof. delivered/failed/pending are operator follow-up
  // states layered on top.
  notices: {
    title: 'Notices',

    labels: {
      body: 'Notice body',
      channel: 'Channel',
      recipient: 'Recipient',
      status: 'Status',
      detail: 'Detail',
    },

    channels: {
      liff: 'LIFF (in-app)',
      line: 'LINE',
      email: 'Email',
      post: 'Post',
    },

    delivery: {
      delivered: 'Delivered',
      failed: 'Failed',
      pending: 'Pending',
    },

    counts: {
      // i18next _one / _other explicit plural branches (CLAUDE.md
      // rule — never use `defaultValue` to mask a missing key).
      audience_one: '{{count}} recipient',
      audience_other: '{{count}} recipients',
      delivered_one: '{{count}} delivered',
      delivered_other: '{{count}} delivered',
      failed_one: '{{count}} failed',
      failed_other: '{{count}} failed',
      pending_one: '{{count}} pending',
      pending_other: '{{count}} pending',
    },

    actions: {
      mark_delivered: 'Mark delivered',
      mark_failed: 'Mark failed',
      clear: 'Reset',
    },

    help: {
      materialise:
        'Saving creates one delivery row per audience member — every active OwnerContract for AGM/EGM, or every active committee member for committee meetings.',
      failed_reason_prompt: 'Reason the delivery failed (e.g., "LINE 400", "email bounced", "post returned"):',
    },

    messages: {
      delivery_state_conflict:
        'A delivery cannot be both delivered and failed; pick one (proof is delivered_at OR failed_reason, never both).',
    },
  },

  // EP-135 Tier 1 — gate messages for the accountant-blocked routes
  errors: {
    condominium_required:
      'This page is only available when Condominium is enabled in account settings.',
  },

  // EP-135 Tier 3 — Attendees (per-meeting roll snapshot). The
  // operator uses this block to check-in attendees, assign proxies and
  // watch the meeting weight before opening the vote.
  attendees: {
    title: 'Attendees',
    description: 'Per-meeting snapshot of the OwnerContract roll — frozen at notice time.',

    labels: {
      customer: 'Owner',
      presence: 'Presence',
      weight: 'Weight',
      units: 'Units',
      proxy_holder: 'Proxy holder',
      proxy_document: 'Proxy document',
      checked_in_at: 'Checked in',
    },

    presences: {
      absent: 'Absent',
      remote: 'Remote',
      in_person: 'In person',
      proxy: 'By proxy',
    },

    actions: {
      snapshot: 'Snapshot roll',
      add: 'Add attendee',
      mark_absent: 'Mark absent',
      mark_remote: 'Mark remote',
      mark_in_person: 'Mark in person',
      mark_proxy: 'Mark by proxy',
    },

    messages: {
      snapshot_done_one: 'Snapshotted {{count}} attendee from the active roll.',
      snapshot_done_other: 'Snapshotted {{count}} attendees from the active roll.',
      snapshot_existing: 'Already snapshotted — no new attendees created.',
    },

    empty: {
      no_attendees: 'No attendees yet',
      no_attendees_description:
        'Snapshot the roll to freeze the weight at notice time, then check attendees in as they arrive.',
    },
  },

  // EP-135 Tier 3 — Voting. Each resolution owns its own ballot block:
  // operator-side "Record vote" form (cast_via: recorded) + tally view
  // (the snapshot the chair signs against). §4a + D-2 — secret
  // resolutions hide the voter↔choice link in the audit log; the
  // receipt_hash + aggregate weight stay visible.
  votes: {
    title: 'Voting',
    ballot_title: 'Ballots',

    labels: {
      attendee: 'Attendee',
      choice: 'Choice',
      weight: 'Weight',
      cast_at: 'Cast at',
      cast_via: 'Cast via',
      surface: 'Surface',
      identity_level: 'Identity',
      receipt_hash: 'Receipt hash',
      recorded_by: 'Recorded by',
      filter_choice: 'Filter by choice',
    },

    choices: {
      for: 'For',
      against: 'Against',
      abstain: 'Abstain',
    },

    cast_via: {
      self: 'Self (app)',
      recorded: 'Recorded (operator)',
    },

    surfaces: {
      liff: 'LIFF',
      web_admin: 'Web admin',
    },

    identity_levels: {
      dipchip: 'Dipchip',
      passport: 'Passport',
      none: 'Unverified',
    },

    actions: {
      record_vote: 'Record vote',
      choose_attendee: 'Choose attendee',
    },

    tally: {
      title: 'Tally',
      for: 'For',
      against: 'Against',
      abstain: 'Abstain',
      tied: 'Tied',
      decision: 'Decision',
      total_cast: 'Total cast',
      total_attendees: 'Total attendees',
      denominator: 'Denominator',
      threshold: 'Threshold',
      decision_passed: 'Passed',
      decision_defeated: 'Defeated',
      snapshot_at: 'Snapshot at',
      no_snapshot: 'Voting has not been closed yet — the tally will be frozen on close.',
      electronic_voting_disabled:
        'Electronic voting is disabled in this account’s ข้อบังคับ — recording a vote is unavailable.',
    },

    audit_log: {
      download: 'Download JSON',
      title: 'Audit log (ETDA ขมธอ. 26-2564)',
      description:
        'Immutable ballot export the chair + secretary walk through before signing the certificate. Secret ballots hide attendee identity at the serializer.',
      electronic_voting_permitted: 'Electronic voting permitted',
      no_ballots: 'No ballots have been recorded.',
    },

    messages: {
      secret_no_identity:
        'This is a secret ballot — the voter↔choice link is hidden at the serializer (§4a).',
    },

    empty: {
      no_votes: 'No ballots yet',
      no_votes_description:
        'Cast a recorded ballot from the operator side, or wait for owners to vote in the customer app.',
    },
  },

  // EP-135 Tier 4 — minutes generation + e-sign flow. The chair and
  // secretary each stamp once (§8 Q-5 dual sign); once both have
  // stamped, the meeting transitions to `certified` atomically.
  minutes: {
    title: 'Minutes',
    description:
      'Generated from the meeting record + frozen tally_snapshot. Draft until the chair AND secretary have both e-signed (§8 Q-5).',
    draft_banner: 'DRAFT — unsigned; awaiting chair + secretary e-sign.',
    certified_banner: 'Certified — chair and secretary e-signed.',
    sections: {
      header: 'Header',
      attendance: 'Attendance',
      resolutions: 'Resolutions',
      certification: 'Certification',
    },
    actions: {
      view_pdf: 'View certificate PDF',
      sign_chair: 'Sign as chair',
      sign_secretary: 'Sign as secretary',
      reset_chair_signature: 'Reset chair signature',
      reset_secretary_signature: 'Reset secretary signature',
    },
    signing: {
      chair_signed_at: 'Chair signed at',
      secretary_signed_at: 'Secretary signed at',
      pending_chair: 'Awaiting chair e-sign.',
      pending_secretary: 'Awaiting secretary e-sign.',
      both_signed: 'Both signers recorded — meeting is certified.',
      not_ready: 'Close voting before generating minutes.',
      pdf_key_label: 'Certificate PDF key',
    },
    errors: {
      already_certified: 'This meeting is certified and cannot be re-signed.',
      not_ready: 'Meeting must be closed or voting_closed before signing.',
      invalid_role: 'Role must be chair or secretary.',
      signer_not_found: 'The signer does not belong to this account.',
      reset_failed: 'Could not reset the signature.',
    },
    messages: {
      signed_chair: 'Chair signature recorded.',
      signed_secretary: 'Secretary signature recorded — meeting certified.',
      reset_chair: 'Chair signature cleared.',
      reset_secretary: 'Secretary signature cleared.',
    },
  },
}
